import type {
  NormalizedWidgetConfig,
  WidgetHandoffResult,
  WidgetMessage,
  WidgetSource,
  WidgetStreamResult,
} from "./types";

type StreamEvent =
  | { type: "progress"; stage?: "accepted" }
  | { type: "token"; content?: string }
  | { type: "message_end" }
  | {
      type: "done";
      sources?: WidgetSource[];
      suggestions?: string[];
      cache_hit?: boolean;
      handoff?: WidgetStreamResult["handoff"];
    }
  | { type: "error"; message?: string };

const MAX_TRANSPORT_ATTEMPTS = 2;
const RETRY_DELAY_MS = 200;
const MAX_USER_MESSAGE_CHARS = 300;
let transportTurnOrdinal = 0;

class ChatHttpError extends Error {
  /**
   * Retains the upstream status so only transient responses are retried.
   */
  constructor(readonly status: number) {
    super(`Chat request failed with status ${String(status)}.`);
    this.name = "ChatHttpError";
  }
}

class ChatTimeoutError extends Error {
  /**
   * Identifies a locally enforced transport timeout.
   */
  constructor(message: string) {
    super(message);
    this.name = "ChatTimeoutError";
  }
}

class IncompleteChatStreamError extends Error {
  /**
   * Identifies a connection that closed without the required done event.
   */
  constructor() {
    super("The assistant response ended before completion.");
    this.name = "IncompleteChatStreamError";
  }
}

interface AttemptState {
  sawEvent: boolean;
}

/**
 * Streams one chat answer from the backend and forwards text deltas as they
 * arrive. One transient failure is retried only when no SSE event was received.
 */
export async function streamChat(
  config: NormalizedWidgetConfig,
  sessionId: string,
  message: string,
  history: WidgetMessage[],
  signal: AbortSignal,
  onToken: (token: string) => void,
  suggestionSelected = false,
): Promise<WidgetStreamResult> {
  transportTurnOrdinal = (transportTurnOrdinal % 999_999) + 1;
  const turnId = `turn-${String(transportTurnOrdinal).padStart(6, "0")}`;
  const body = JSON.stringify({
    message,
    sessionId,
    responseMode: config.backend.responseMode,
    sourceCount: config.backend.sourceCount,
    ...(suggestionSelected ? { suggestionSelected: true } : {}),
    // Keep enough recent turns for non-personal academic context such as a
    // stated qualification to survive a longer programme chat.
    messages: history
      .filter(
        (entry) =>
          entry.origin !== "inactivity" &&
          (entry.role === "user" || entry.status !== "interrupted"),
      )
      .slice(-40)
      .map((entry) => ({
        role: entry.role,
        content:
          entry.role === "user"
            ? entry.content.slice(0, MAX_USER_MESSAGE_CHARS)
            : entry.content,
      })),
    ...(config.lead.programInterested
      ? { lead: { programInterested: config.lead.programInterested } }
      : {}),
  });

  for (let attempt = 0; attempt < MAX_TRANSPORT_ATTEMPTS; attempt += 1) {
    const state: AttemptState = { sawEvent: false };
    try {
      const result = await streamAttempt(
        config,
        sessionId,
        body,
        turnId,
        signal,
        onToken,
        state,
      );
      result.transportRetries = attempt;
      return result;
    } catch (error) {
      if (signal.aborted) {
        throw new DOMException("Chat request was cancelled.", "AbortError");
      }
      const canRetry =
        attempt + 1 < MAX_TRANSPORT_ATTEMPTS &&
        !state.sawEvent &&
        isTransientPreStreamFailure(error);
      if (!canRetry) {
        throw error;
      }
      await waitForRetry(signal);
    }
  }

  throw new Error("The assistant could not answer.");
}

/** Prepares one backend-owned, context-aware inactivity continuation. */
export async function requestFollowUp(
  config: NormalizedWidgetConfig,
  sessionId: string,
  history: WidgetMessage[],
  followUpCount: number,
): Promise<string | null> {
  const response = await fetch(`${config.backend.baseUrl}/api/chat/follow-up`, {
    method: "POST",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
      "x-debot-session-id": sessionId,
    },
    body: JSON.stringify({
      sessionId,
      followUpCount,
      messages: history
        .filter(
          (entry) =>
            entry.origin !== "inactivity" &&
            (entry.role === "user" || entry.status !== "interrupted"),
        )
        .slice(-40)
        .map((entry) => ({
          role: entry.role,
          content:
            entry.role === "user"
              ? entry.content.slice(0, MAX_USER_MESSAGE_CHARS)
              : entry.content.slice(0, 1_200),
        })),
      ...(config.lead.programInterested
        ? { programInterested: config.lead.programInterested }
        : {}),
    }),
    signal: AbortSignal.timeout(3_500),
  });
  if (!response.ok) throw new Error("Follow-up preparation failed.");
  const payload = (await response.json()) as Record<string, unknown>;
  if (payload.suppressed === true) return null;
  const message =
    typeof payload.message === "string"
      ? payload.message.replace(/\s+/g, " ").trim().slice(0, 150)
      : "";
  if (!message) throw new Error("Follow-up preparation was empty.");
  return message;
}

/**
 * Executes one fetch and parses its SSE stream without retrying internally.
 */
async function streamAttempt(
  config: NormalizedWidgetConfig,
  sessionId: string,
  body: string,
  turnId: string,
  signal: AbortSignal,
  onToken: (token: string) => void,
  state: AttemptState,
): Promise<WidgetStreamResult> {
  const requestController = new AbortController();
  let timeoutMessage = "";
  let firstEventTimer: ReturnType<typeof setTimeout> | undefined;
  let requestTimer: ReturnType<typeof setTimeout> | undefined;
  const abortFromCaller = (): void => requestController.abort(signal.reason);
  if (signal.aborted) {
    abortFromCaller();
  } else {
    signal.addEventListener("abort", abortFromCaller, { once: true });
  }

  /**
   * Aborts with an actionable timeout reason instead of a generic network
   * exception.
   */
  const abortForTimeout = (message: string): void => {
    timeoutMessage = message;
    requestController.abort();
  };
  firstEventTimer = setTimeout(
    () =>
      abortForTimeout(
        "The assistant did not begin responding in time. Please retry.",
      ),
    config.backend.firstEventTimeoutMs,
  );
  requestTimer = setTimeout(
    () =>
      abortForTimeout(
        "The assistant response exceeded the time limit. Please retry.",
      ),
    config.backend.requestTimeoutMs,
  );

  const result: WidgetStreamResult = {
    sources: [],
    suggestions: [],
    cacheHit: false,
    transportRetries: 0,
  };
  let sawDone = false;

  /**
   * Parses and applies one complete SSE data block.
   */
  const applyBlock = (block: string): void => {
    const data = block
      .split(/\r?\n/)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trimStart())
      .join("\n");
    if (!data.trim()) {
      return;
    }

    // Any data event means the backend started a response. Never automatically
    // replay after this point because doing so could duplicate visible text.
    state.sawEvent = true;
    const event = JSON.parse(data) as StreamEvent;
    if (event.type === "progress") {
      // The backend accepted this POST. Keep waiting on the same execution
      // without exposing transport state as an assistant message.
      return;
    }
    if (event.type === "token" && event.content) {
      onToken(event.content);
      return;
    }
    if (event.type === "message_end") {
      // Ignore legacy boundaries so one user turn always stays one reply.
      return;
    }
    if (event.type === "error") {
      throw new Error(event.message || "The assistant could not answer.");
    }
    if (event.type === "done") {
      result.sources = Array.isArray(event.sources) ? event.sources : [];
      result.suggestions = sanitizeSuggestions(event.suggestions);
      result.cacheHit = event.cache_hit ?? false;
      if (event.handoff?.suggested === true) {
        result.handoff = event.handoff;
      }
      sawDone = true;
    }
  };

  try {
    const response = await fetch(`${config.backend.baseUrl}/api/chat/message`, {
      method: "POST",
      headers: {
        accept: "text/event-stream",
        "content-type": "application/json",
        "x-debot-session-id": sessionId,
        "x-debot-conversation-id": sessionId,
        "x-debot-turn-id": turnId,
      },
      body,
      signal: requestController.signal,
    });
    if (!response.ok) {
      throw new ChatHttpError(response.status);
    }
    if (!response.body) {
      throw new Error("Chat response was empty.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (value?.byteLength) {
        clearTimeout(firstEventTimer);
        firstEventTimer = undefined;
      }
      buffer += decoder.decode(value, { stream: !done });
      const blocks = buffer.split(/\r?\n\r?\n/);
      buffer = blocks.pop() ?? "";
      for (const block of blocks) {
        applyBlock(block);
      }
      if (done) {
        if (buffer.trim()) {
          applyBlock(buffer);
        }
        if (!sawDone) {
          throw new IncompleteChatStreamError();
        }
        // The reader is already at EOF. Calling releaseLock() here makes
        // Chromium report this successful fetch as net::ERR_ABORTED.
        return result;
      }
    }
  } catch (error) {
    if (timeoutMessage) {
      throw new ChatTimeoutError(timeoutMessage);
    }
    if (signal.aborted) {
      throw new DOMException("Chat request was cancelled.", "AbortError");
    }
    throw error;
  } finally {
    clearTimeout(firstEventTimer);
    clearTimeout(requestTimer);
    signal.removeEventListener("abort", abortFromCaller);
  }
}

/** Keeps backend-provided quick questions compact and presentation-safe. */
function sanitizeSuggestions(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(
      value
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.replace(/\s+/g, " ").trim().slice(0, 100))
        .filter(Boolean),
    ),
  ].slice(0, 2);
}

/** Sends complete lead details only after an explicit user handoff action. */
export async function requestHandoff(
  config: NormalizedWidgetConfig,
  sessionId: string,
  history: WidgetMessage[],
  signal: AbortSignal,
): Promise<WidgetHandoffResult> {
  if (!config.handoff.enabled || !hasCompleteLead(config)) {
    return { status: "unavailable" };
  }
  const controller = new AbortController();
  const abortFromCaller = (): void => controller.abort(signal.reason);
  if (signal.aborted) abortFromCaller();
  else signal.addEventListener("abort", abortFromCaller, { once: true });
  const timer = setTimeout(() => controller.abort(), config.handoff.timeoutMs);
  try {
    const response = await fetch(`${config.backend.baseUrl}/api/handoff`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
        "x-debot-session-id": sessionId,
      },
      body: JSON.stringify({
        consent: true,
        sessionId,
        lead: config.lead,
        messages: history.slice(-6).map((entry) => ({
          role: entry.role,
          content: entry.content.slice(0, 600),
        })),
      }),
      signal: controller.signal,
    });
    if (!response.ok) return { status: "unavailable" };
    return normalizeHandoffResult(await response.json());
  } catch {
    return { status: "unavailable" };
  } finally {
    clearTimeout(timer);
    signal.removeEventListener("abort", abortFromCaller);
  }
}

/** Confirms all host-provided contact fields are present before rendering consent UI. */
export function hasCompleteLead(config: NormalizedWidgetConfig): boolean {
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.lead.email);
  const mobileDigits = config.lead.mobile.replace(/\D/g, "");
  return Boolean(
    config.lead.name &&
      emailValid &&
      mobileDigits.length >= 7 &&
      mobileDigits.length <= 15 &&
      config.lead.programInterested,
  );
}

/** Keeps the backend handoff response within the small trusted widget contract. */
function normalizeHandoffResult(payload: unknown): WidgetHandoffResult {
  if (typeof payload !== "object" || payload === null) {
    return { status: "unavailable" };
  }
  const raw = payload as Record<string, unknown>;
  if (raw.status === "busy") return { status: "busy" };
  if (raw.status !== "connected") return { status: "unavailable" };
  const result: WidgetHandoffResult = { status: "connected" };
  const handoffId = safeText(raw.handoffId, 120);
  const agentName = safeText(raw.agentName, 80);
  const connectionUrl = safeHttpsUrl(raw.connectionUrl);
  if (handoffId) result.handoffId = handoffId;
  if (agentName) result.agentName = agentName;
  if (connectionUrl) result.connectionUrl = connectionUrl;
  return result;
}

/** Binds provider copy to one plain line. */
function safeText(value: unknown, maxChars: number): string {
  return typeof value === "string"
    ? value.replace(/\s+/g, " ").trim().slice(0, maxChars)
    : "";
}

/** Allows navigation only to an HTTPS provider URL. */
function safeHttpsUrl(value: unknown): string {
  if (typeof value !== "string") return "";
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : "";
  } catch {
    return "";
  }
}

/**
 * Retries only failures that can recover without changing the request.
 */
function isTransientPreStreamFailure(error: unknown): boolean {
  if (error instanceof ChatTimeoutError) {
    return true;
  }
  if (error instanceof IncompleteChatStreamError) {
    return true;
  }
  if (error instanceof ChatHttpError) {
    return (
      error.status === 408 ||
      error.status === 425 ||
      (error.status >= 500 && error.status <= 599)
    );
  }
  return (
    error instanceof TypeError ||
    (error instanceof DOMException && error.name === "NetworkError")
  );
}

/**
 * Waits briefly for a local proxy or forwarded port to become ready.
 */
async function waitForRetry(signal: AbortSignal): Promise<void> {
  if (signal.aborted) {
    throw new DOMException("Chat request was cancelled.", "AbortError");
  }
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, RETRY_DELAY_MS);
    const abort = (): void => {
      clearTimeout(timer);
      reject(new DOMException("Chat request was cancelled.", "AbortError"));
    };
    signal.addEventListener("abort", abort, { once: true });
  });
}
